<template>
  <Teleport :to="store.overlayTarget">
    <!-- Backdrop -->
    <Transition name="fade">
      <div v-if="open" class="fixed inset-0 bg-black/30 z-[70]" @click="emit('close')"></div>
    </Transition>

    <!-- Modal: flex-centered wrapper so the card never moves, only fades/scales in place -->
    <Transition name="pop">
      <div v-if="open && booking" class="fixed inset-0 z-[80] flex items-center justify-center p-4 pointer-events-none">
        <div
          class="pointer-events-auto w-[480px] max-w-full max-h-full flex flex-col bg-white dark:bg-[#1C2128] rounded-xl shadow-2xl border border-gray-100 dark:border-gray-700"
          @click.stop
        >
        <!-- Header -->
        <div class="flex items-center justify-between px-5 h-14 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
          <h3 class="text-base font-semibold text-gray-900 dark:text-white">Private notes</h3>
          <button
            @click="emit('close')"
            class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Close"
          >
            <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div class="p-5 overflow-y-auto">
          <!-- Privacy notice -->
          <div class="flex items-start gap-2.5 px-3 py-2.5 mb-4 bg-gray-50 dark:bg-gray-800/60 rounded-lg">
            <svg class="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
            <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              These notes are private to you in Timelydo. They are never shared with attendees or with Google Calendar.
            </p>
          </div>

          <!-- Toolbar -->
          <div class="flex items-center gap-0.5 px-1.5 py-1 border border-gray-200 dark:border-gray-600 border-b-0 rounded-t-lg bg-gray-50 dark:bg-gray-800/60">
            <button
              v-for="tool in TOOLS"
              :key="tool.command"
              @mousedown.prevent="applyTool(tool)"
              :title="tool.title"
              class="w-8 h-8 flex items-center justify-center rounded text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              :class="tool.textClass"
            >
              <span v-if="tool.label">{{ tool.label }}</span>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="tool.icon"/>
              </svg>
            </button>
          </div>

          <!-- Editor -->
          <div
            ref="editorEl"
            contenteditable="true"
            class="min-h-[140px] max-h-[300px] overflow-y-auto px-3 py-2.5 text-sm text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 rounded-b-lg focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400
                   [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-blue-600 [&_a]:underline"
            data-placeholder="Write a note only you can see"
          ></div>

          <p v-if="error" class="text-xs text-red-500 dark:text-red-400 mt-2">{{ error }}</p>
        </div>

        <!-- Footer: scope question for recurring bookings, otherwise plain save -->
        <div v-if="askScope" class="px-5 py-3 border-t border-gray-100 dark:border-gray-700 flex-shrink-0">
          <p class="text-sm text-gray-600 dark:text-gray-300 mb-2.5">This booking repeats. Where should this note apply?</p>
          <div class="flex items-center justify-end gap-2">
            <button
              @click="askScope = false"
              class="px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              Back
            </button>
            <button
              @click="performSave(false)"
              :disabled="saving"
              class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-60 transition-colors"
            >
              Only this booking
            </button>
            <button
              @click="performSave(true)"
              :disabled="saving"
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg transition-colors"
            >
              All in the series
            </button>
          </div>
        </div>
        <div v-else class="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 dark:border-gray-700 flex-shrink-0">
          <button
            @click="emit('close')"
            class="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            @click="save"
            :disabled="saving"
            class="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {{ saving ? 'Saving...' : 'Save note' }}
          </button>
        </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue'
import { useCalendarStore } from 'stores/calendar.js'
import { useRequest } from 'composables/useRequest.js'

const props = defineProps({
  open:    { type: Boolean, default: false },
  booking: { type: Object,  default: null  },
})

const emit = defineEmits(['close'])

const store = useCalendarStore()
const { axiosRequest } = useRequest()

const editorEl = ref(null)
const saving   = ref(false)
const error    = ref(null)
const askScope = ref(false)

const TOOLS = [
  { command: 'bold',               title: 'Bold',          label: 'B', textClass: 'font-bold text-[13px]' },
  { command: 'italic',             title: 'Italic',        label: 'I', textClass: 'italic font-serif text-[13px]' },
  { command: 'underline',          title: 'Underline',     label: 'U', textClass: 'underline text-[13px]' },
  { command: 'insertUnorderedList', title: 'Bullet list',  icon: 'M4 6h.01M4 12h.01M4 18h.01M8 6h12M8 12h12M8 18h12' },
  { command: 'insertOrderedList',   title: 'Numbered list', icon: 'M7 6h13M7 12h13M7 18h13M3.5 5.5L4.5 5v3M3.5 17.5h1.5l-1.5 2h1.5' },
]

function applyTool(tool) {
  editorEl.value?.focus()
  document.execCommand?.(tool.command, false, null)
}

watch(() => props.open, async (open) => {
  if (!open) return
  error.value = null
  askScope.value = false
  await nextTick()
  if (editorEl.value) {
    editorEl.value.innerHTML = props.booking?.internal_notes || ''
    editorEl.value.focus()
  }
})

const isExternal  = () => props.booking?.context === 'ExternalBooking'
const isRecurring = () => isExternal() && !!props.booking?.['recurring?']

function save() {
  if (!props.booking || saving.value) return
  // Recurring bookings: ask whether the note is for this instance or the series
  if (isRecurring()) {
    askScope.value = true
    return
  }
  performSave(false)
}

async function performSave(applyToSeries) {
  if (!props.booking || saving.value) return
  const html    = editorEl.value?.innerHTML?.trim() || ''
  const isEmpty = !editorEl.value?.textContent?.trim()
  const notes   = isEmpty ? null : html

  saving.value = true
  error.value  = null
  try {
    let res
    if (isExternal()) {
      res = notes === null
        ? await axiosRequest('DELETE', `/api/v1/connected_calendar_bookings_internal_notes/${props.booking.id}`,
            { apply_to_series: applyToSeries }, false)
        : await axiosRequest('PUT', `/api/v1/connected_calendar_bookings_internal_notes/${props.booking.id}`,
            { connected_calendar_booking_internal_notes: { internal_notes: notes }, apply_to_series: applyToSeries }, false)
    } else {
      res = await axiosRequest('PUT', `/api/v1/bookings/${props.booking.id}`,
        { booking: { internal_notes: notes } }, false)
    }

    if (!res.js_error && res.response?.success) {
      if (applyToSeries) {
        store.updateSeriesNotes(props.booking.recurring_booking_id, props.booking.calendar_id, notes)
      }
      store.updateBookingNotes(props.booking.id, notes)
      emit('close')
    } else {
      error.value = res.response?.message || 'Could not save the note.'
    }
  } catch {
    error.value = 'Something went wrong.'
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
[contenteditable]:empty::before {
  content: attr(data-placeholder);
  color: #9ca3af;
}
.fade-enter-active, .fade-leave-active { transition: opacity 0.15s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.pop-enter-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.pop-leave-active { transition: opacity 0.1s ease, transform 0.1s ease; }
.pop-enter-from, .pop-leave-to { opacity: 0; transform: scale(0.96); }
</style>
